<template>
    <div class="subscription-selector-wrapper">
        <div class="subscription-desc-header">
            <p class="desc-title">Plans</p>
            <div class="billing-cycle-wrapper">
                <p class="billing-title">Billed</p>
                <div class="billing-cycles">
                    <span
                        v-for="(cycle, indx) in subscriptionIntervals"
                        :key="indx"
                        :style="cycleSelectedStyle(cycle)"
                        @click="selectBillingCycle(cycle.interval)"
                        class="billing-cycle"
                    >
                        {{ cycle.name }}
                    </span>
                </div>
            </div>
        </div>
        <div class="subscription-desc-content">
            <div
                v-for="(plan, indx) in plans"
                :style="planWrapperStyle(plan)"
                :key="indx"
                @click="selectPlan(plan.name)"
                class="plan-wrapper"
            >
                <div class="plan-header">
                    <div class="plan-select-wrapper">
                        <img :src="selectorSrc(plan)" alt="" class="selector" />
                        <p :style="selectableStyle(plan)" class="plan-name">
                            {{ plan.name }}
                        </p>
                    </div>
                    <div class="price-wrapper">
                        <div v-if="plan.selectable" class="prices">
                            <p
                                :style="intervalStyle(selectedCycle, 'month')"
                                class="month-cycle-price"
                            >
                                ${{ monthPrice(plan.name) }}
                            </p>
                            <p
                                :style="intervalStyle(selectedCycle, 'year')"
                                class="year-cycle-price"
                            >
                                ${{ yearPrice(plan.name) }}
                            </p>
                        </div>
                        <div v-else class="prices">
                            <p :style="{ color: '#464646' }" class="year-cycle-price">
                                ${{ yearPrice(plan.name) }}
                            </p>
                        </div>
                        <p :style="selectableStyle(plan)" class="cycle">Per month</p>
                    </div>
                </div>
                <div v-if="plan.selected" class="plan-content">
                    <p class="plan-descriptor">
                        {{ plan.descriptor }}
                    </p>
                    <div
                        v-for="(feature, indx) in plan.features"
                        :key="indx"
                        class="plan-feature"
                    >
                        <div v-if="feature.name" class="feature">
                            <div class="feature-wrapper">
                                <img
                                    :src="URLS.icons.yellowCheck"
                                    alt="yellow-check"
                                    class="yellow-check"
                                />
                                <p class="feature-name">
                                    {{ feature.name }}
                                </p>
                            </div>
                            <div
                                v-for="(subfeature, indx) in feature.subfeatures"
                                :key="indx"
                                class="plan-subfeature"
                            >
                                <img
                                    :src="URLS.icons.yellowCheck"
                                    alt="yellow-check"
                                    class="yellow-check"
                                />
                                <p class="feature-name">
                                    {{ subfeature.name }}
                                </p>
                            </div>
                        </div>
                        <div v-else class="feature">
                            <div
                                v-for="(subfeature, indx) in feature.subfeatures"
                                :key="indx"
                                class="feature-wrapper"
                            >
                                <img
                                    :src="URLS.icons.yellowCheck"
                                    alt="yellow-check"
                                    class="yellow-check"
                                />
                                <p class="feature-name">
                                    {{ subfeature.name }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <a
            :href="`/profile/stripe/plans/${role}`"
            target="_blank"
            class="subscription-plans-link"
        >
            Subscriptions plans
        </a>
    </div>
</template>
<script>
    import { useSubscription } from '@/composables/features/subscriptions/useSubscriptions'
    const URLS = {
        icons: {
            yellowCheck: '/images/global/icon-yes-art.png',
            emptySelector: '/images/icons/selector-empty-white.svg',
            emptyUnselectableSelector: '/images/icons/selector-empty.svg',
            filledSelector: '/images/icons/selector-filled.svg'
        }
    }

    export default {
        name: 'SubscriptionSelector',
        props: {
            role: {
                type: String,
                required: true
            }
        },
        setup(props, context) {
            const {
                plans,
                subscriptionIntervals,
                selectedCycle,
                monthPrice,
                monthTotalPrice,
                yearPrice,
                yearTotalPrice,
                selectBillingCycle,
                selectPlan
            } = useSubscription(props, context)

            return {
                plans,
                subscriptionIntervals,
                selectedCycle,
                monthPrice,
                monthTotalPrice,
                yearPrice,
                yearTotalPrice,
                selectPlan,
                selectBillingCycle
            }
        },
        computed: {
            URLS: () => URLS,
            cycleSelectedStyle: () => (cycle) =>
                cycle.selected ? { color: 'black', background: '#cdeb09' } : {},
            selectableStyle: () => (plan) => !plan.selectable ? { color: '#464646' } : {},
            intervalStyle: () => (cycle, interval) =>
                cycle.interval !== interval
                    ? {
                          color: '#8b8b8b',
                          'text-decoration-line': 'line-through'
                      }
                    : {},
            planWrapperStyle: () => (plan) => {
                if (plan.selected) {
                    return { border: '1px solid #cdeb09' }
                } else if (!plan.selectable) {
                    return { border: '1px solid #464646' }
                } else return {}
            },
            selectorSrc: (self) => (plan) => {
                if (!plan.selectable) {
                    return self.URLS.icons.emptyUnselectableSelector
                } else if (plan.selected) {
                    return self.URLS.icons.filledSelector
                } else return self.URLS.icons.emptySelector
            }
        }
    }
</script>
<style lang="scss" scoped>
    p {
        margin-bottom: 0px;
    }
    button {
        border: none;
    }
    .subscription-selector-wrapper {
        display: flex;
        flex-direction: column;
        width: 100%;
        max-width: 380px;
        justify-content: center;
        align-items: center;
        gap: 24px;

        .subscription-desc-header {
            width: 100%;
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;

            .desc-title {
                color: #dedede;
                text-align: center;
                font-family: 'Instrument Sans';
                font-size: 32px;
                font-style: normal;
                font-weight: 500;
                line-height: 120%;
                letter-spacing: -0.64px;
            }
            .billing-cycle-wrapper {
                display: flex;
                flex-direction: row;
                justify-content: center;
                align-items: center;
                gap: 12px;

                .billing-title {
                    color: #acacac;
                    text-align: center;
                    font-family: 'Instrument Sans';
                    font-size: 14px;
                    font-style: normal;
                    font-weight: 700;
                    line-height: 130%;
                    letter-spacing: 0.14px;
                }
                .billing-cycles {
                    display: flex;
                    flex-direction: row;
                    justify-content: center;
                    align-items: center;
                    gap: 8px;
                    border-radius: 12px;
                    border: 1px solid #464646;
                    padding: 4px;

                    .billing-cycle {
                        padding: 8px 12px;
                        border-radius: 8px;
                        color: #8b8b8b;
                        font-family: 'Instrument Sans';
                        font-size: 14px;
                        font-style: normal;
                        font-weight: 500;
                        line-height: 115%;
                        letter-spacing: 0.14px;
                        cursor: pointer;
                    }
                }
            }
        }
        .subscription-desc-content {
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 16px;

            .plan-wrapper {
                width: 100%;
                padding: 16px;
                border-radius: 15px;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                gap: 8px;
                border-radius: 15px;
                border: 1px solid #8b8b8b;
                cursor: pointer;

                .plan-header {
                    width: 100%;
                    display: flex;
                    flex-direction: row;
                    justify-content: space-between;
                    align-items: center;
                    gap: 16px;

                    .plan-select-wrapper {
                        display: flex;
                        flex-direction: row;
                        justify-content: center;
                        align-items: center;
                        gap: 16px;
                        .selector {
                            width: 20px;
                            height: 20px;
                            object-fit: cover;
                        }
                        .plan-name {
                            width: 100%;
                            color: #acacac;
                            font-family: 'Instrument Sans';
                            font-size: 18px;
                            font-style: normal;
                            font-weight: 500;
                            line-height: 133%; /* 23.94px */
                        }
                    }
                    .price-wrapper {
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        align-items: center;
                        gap: 4px;

                        .prices {
                            width: 100%;
                            display: flex;
                            flex-direction: row;
                            justify-content: flex-end;
                            align-items: center;
                            gap: 6px;

                            .month-cycle-price,
                            .year-cycle-price {
                                color: #f5f5f5;
                                text-align: center;
                                font-family: 'Instrument Sans';
                                font-size: 18px;
                                font-style: normal;
                                font-weight: 500;
                                line-height: 133%;
                            }
                        }
                        .cycle {
                            color: #acacac;
                            font-family: 'Instrument Sans';
                            font-size: 14px;
                            font-style: normal;
                            font-weight: 500;
                            line-height: 115%;
                            letter-spacing: 0.14px;
                        }
                    }
                }
                .plan-content {
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: flex-start;
                    gap: 8px;
                    padding-left: 36px;

                    .plan-descriptor {
                        color: #cdeb09;
                        font-family: 'Instrument Sans';
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 500;
                        line-height: 125%;
                    }
                    .plan-feature {
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        align-items: center;
                        gap: 8px;

                        .feature {
                            display: flex;
                            flex-direction: column;
                            justify-content: flex-start;
                            align-items: flex-start;
                            gap: 8px;

                            .feature-wrapper {
                                display: flex;
                                flex-direction: row;
                                justify-content: center;
                                align-items: flex-start;
                                gap: 8px;
                            }

                            .yellow-check {
                                width: 20px;
                                height: 20px;
                                object-fit: cover;
                            }
                            .feature-name {
                                color: #dedede;
                                font-family: 'Instrument Sans';
                                font-size: 16px;
                                font-style: normal;
                                font-weight: 500;
                                line-height: 125%;
                            }
                            .plan-subfeature {
                                display: flex;
                                flex-direction: row;
                                justify-content: flex-start;
                                align-items: center;
                                gap: 8px;
                                padding-left: 28px;
                            }
                        }
                    }
                }
            }
        }
        .subscription-plans-link {
            color: #acacac;
            font-family: 'Instrument Sans';
            font-size: 16px;
            font-style: normal;
            font-weight: 500;
            line-height: 125%;
        }
    }
</style>
